import TimerUtils from '@/helpers/timerUtils'
import type { TDraft, TDraftImportType } from '@/types/drafts'
import { DropboxUrlUtil } from '@/helpers/dropboxUrlUtil'
import {
  ERemoteImportURLStatus,
  ERemoteImportURLType,
  TRemoteImportURL,
  type TUploadedFileInfo,
  type TImportResponse,
} from '@/types/remote-imports'
import { track } from '@/helpers/mixpanelDes'
import { audioVideoImportTrackingProperties } from '@/helpers/audioVideoImportTracking'
import { ImportTypes } from '@/helpers/importTypes'
import { ReadabilityWithIframe } from '@/helpers/readabilityWithIframe'
import { defineStore } from 'pinia'
import { http, HTTPError, getHttpErrorMessage } from '@/helpers/http'
import { ref, reactive, onScopeDispose } from 'vue'
import { useCookies } from '@vueuse/integrations/useCookies'
import { useDraftsStore } from '@/stores/drafts'
import { useSnackbarStore } from '@/stores/snackbar'
import { useLoaderStore } from '@/stores/loader'
import { useFlipbooksStore } from '@/stores/flipbooks'
import { useModalStore } from '@/stores/modal'
import { GDocImport } from '@/helpers/gdocImport'
import HtmlCleaner from '@/helpers/HtmlCleaner'
import { useRouter } from 'vue-router'
import { useCreatorStore } from '@/stores/creator'
import { getGoogleDriveDownloadUrl, getUnsupportedImportUrlMessage, isScheduledVideoUrl } from '@/helpers/url'

export const useRemoteImportStore = defineStore('remote-imports', () => {
  const urls = ref<TRemoteImportURL[]>([])
  const importType = ref<ERemoteImportURLType | null>(null)
  const draftId = ref<number>(-1)
  const draftTitle = ref<string>('New project ' + Math.floor(Date.now() / 1000))
  const draftTags = ref<number[]>([])
  const content = ref<string>('')
  const bookType = ref<string | null>(null)
  const drafts = useDraftsStore()
  const flipbooks = useFlipbooksStore()
  const modal = useModalStore()
  const snack = useSnackbarStore()
  const loader = useLoaderStore()
  const cookies = useCookies()
  const router = useRouter()
  const uploadedFileName = ref<string>('')
  const uploadedFileLink = ref<string>('')
  const pdfImportIntoFlipbookOnly = ref<boolean>(false)
  const processingMessage = ref<string>('Converting ...')
  const PROGRESS_POLL_INTERVAL = 2500
  const MAX_PROGRESS_ERRORS = 25
  const progressTimers = new Map<TRemoteImportURL, NodeJS.Timeout>()
  const progressErrorCounts = new Map<TRemoteImportURL, number>()
  let importErrorId = ''
  const dndUrl = ref<string | null>(null)
  const dndUrlType = ref<ERemoteImportURLType | null>(null)
  let draftChanges: Promise<void> = Promise.resolve()
  let importController = new AbortController()

  function stopProgressTimer(url: TRemoteImportURL) {
    const handle = progressTimers.get(url)
    if (handle !== undefined) {
      clearTimeout(handle)
      progressTimers.delete(url)
    }
    progressErrorCounts.delete(url)
  }

  function queueDraftChange(change: () => Promise<void>): Promise<void> {
    const queued = draftChanges.then(change)
    draftChanges = queued.catch(() => undefined)

    return queued
  }

  function stopAllProgressTimers() {
    progressTimers.forEach((handle) => clearTimeout(handle))
    progressTimers.clear()
    progressErrorCounts.clear()
  }

  function cancelImports() {
    importController.abort()
    importController = new AbortController()
    stopAllProgressTimers()
    snack.remove(importErrorId)
    urls.value = []
    draftId.value = -1
    content.value = ''
    draftTags.value = []
    if (!draftTitle.value.match(/New project \d+/)) {
      draftTitle.value = 'New project ' + Math.floor(Date.now() / 1000)
    }
  }

  function startProgressPolling(url: TRemoteImportURL) {
    stopProgressTimer(url)
    progressErrorCounts.set(url, 0)
    scheduleProgressPoll(url)
  }

  function scheduleProgressPoll(url: TRemoteImportURL) {
    const handle: NodeJS.Timeout = setTimeout(() => {
      void pollProgress(url, handle)
    }, PROGRESS_POLL_INTERVAL)
    progressTimers.set(url, handle)
  }

  async function pollProgress(url: TRemoteImportURL, handle: NodeJS.Timeout) {
    const done = await getDownloadProgress(url)
    if (progressTimers.get(url) !== handle) {
      return
    }
    if (done) {
      stopProgressTimer(url)
      return
    }
    scheduleProgressPoll(url)
  }

  onScopeDispose(() => stopAllProgressTimers())

  router.beforeEach(() => snack.remove(importErrorId))

  function isFileImportType(type?: ERemoteImportURLType): boolean {
    return (
      type === ERemoteImportURLType.DocX ||
      type === ERemoteImportURLType.Flipbook ||
      type === ERemoteImportURLType.Audio ||
      type === ERemoteImportURLType.Pdf ||
      type === ERemoteImportURLType.Pptx ||
      type === ERemoteImportURLType.GSlides ||
      type === ERemoteImportURLType.YouTube ||
      type === ERemoteImportURLType.Video ||
      type === ERemoteImportURLType.GDoc
    )
  }

  function prepareImportedHtml(html: string): string {
    if (!/<h1[\s>]/i.test(html)) {
      return insertPageBreaksBeforeHeadings(html)
    }
    const container = HtmlCleaner.createNode('div', html)
    HtmlCleaner.handleH1HeadingsIfPresent(container, false)

    return insertPageBreaksBeforeHeadings(container.innerHTML)
  }

  function insertPageBreaksBeforeHeadings(html: string): string {
    const startsWithH2 = /^\s*<h2(\s|>)/i.test(html)
    let first = startsWithH2
    return html.replace(/<h2(\s|>)/gi, (match) => {
      if (first) {
        first = false
        return match
      }
      return `<div data-type="pageBreak"></div>${match}`
    })
  }

  function getTitleFromContent(content: string, defaultTitle: string = '(new import)'): string {
    const elementsPriority: string[] = ['h1', 'h2']
    let title: string | null = null

    const contentLower = content.toLowerCase()
    for (const element of elementsPriority) {
      const openTagStart = `<${element}`
      const closeTag = `</${element}>`
      const startIndex = contentLower.indexOf(openTagStart)
      if (startIndex >= 0) {
        const startTagEnd = content.indexOf('>', startIndex)
        const endIndex = content.indexOf(closeTag, startTagEnd)
        if (endIndex > startTagEnd) {
          title = content.substring(startTagEnd + 1, endIndex)
          break
        }
      }
    }
    if (title) {
      return title.trim()
    }
    return defaultTitle
  }

  function init(type: ERemoteImportURLType) {
    importType.value = type
  }

  function addUrl(url: TRemoteImportURL) {
    if (importType.value !== null) {
      const reactiveUrl = reactive(url)
      urls.value.push(reactiveUrl)
      fetchUrl(reactiveUrl)
    } else {
      throw new Error(`Import type not initialized`)
    }
  }

  async function removeUrlOrFile(index: number) {
    const removed = urls.value[index]
    if (removed) {
      stopProgressTimer(removed)
    }
    urls.value.splice(index, 1)
    await queueDraftChange(async () => {
      const first = urls.value.find((u) => u.content)
      if (first) {
        content.value = urls.value.map((u) => u.content ?? '').join('\n')
        draftId.value = await drafts.save(
          draftId.value >= 0 ? draftId.value : null,
          first.title ?? '',
          content.value,
          first.url ?? '',
          resolveImportType(first),
        )
        return
      }
      if (draftId.value >= 0) {
        const draftToDelete = { id: draftId.value, name: '' } as TDraft
        await drafts.remove(draftToDelete, 'item-removed-from-imports')
        draftId.value = -1
        content.value = ''
      }
    })
  }

  /*
    function addFiles(files: File[] | null) {
      if (importType.value !== null && files && files.length > 0) {
        files.forEach((f) => importType.value && urls.value.push(new TRemoteImportURL(importType.value, '', f)))
      } else {
        throw new Error(`Import type not initialized`)
      }
    }
    */

  async function fetchFromWeb(url: TRemoteImportURL) {
    const gdoc = new GDocImport()
    const isGdoc = gdoc.isGDocUrl(url.url)
    const trackEventName: string = isGdoc ? 'imported-google-document' : 'imported-article'
    const trackErrorEventName: string = isGdoc ? 'error-importing-google-document' : 'imported-article-failed'
    const signal = importController.signal
    try {
      url.status = ERemoteImportURLStatus.Fetching
      url.progress = 0

      let result: { data: { status?: string; content?: string; title?: string; url?: string; message?: string } }

      if (isGdoc) {
        const gdocResult = await gdoc.getHtmlFromGDocValidUrl(url.url, window.location.origin, signal)
        if (signal.aborted) {
          return
        }
        if (gdocResult.html) {
          url.content = prepareImportedHtml(gdocResult.html)
          url.title = ''
          result = { data: { status: 'success', content: gdocResult.html, title: '', url: url.url } }
        } else {
          track(trackErrorEventName, {
            url: url.url,
            info: gdocResult.message ?? 'Error fetching Google Doc',
            ...(gdocResult.status ? { status: gdocResult.status } : {}),
            ...(gdocResult.redirected ? { redirected: true } : {}),
          })
          snack.add(gdocResult.message ?? 'Error when fetching GDoc', { type: 'danger', permanent: true })
          url.status = ERemoteImportURLStatus.Error
          return
        }
      } else {
        const unsupportedMessage = getUnsupportedImportUrlMessage(url.url)
        if (unsupportedMessage) {
          track(trackErrorEventName, { url: url.url, info: unsupportedMessage })
          snack.add(unsupportedMessage, { type: 'danger', permanent: true })
          url.status = ERemoteImportURLStatus.Error
          return
        }
        const driveDownloadUrl = getGoogleDriveDownloadUrl(url.url)
        if (driveDownloadUrl) {
          url.url = driveDownloadUrl
        }
        const resource = new ReadabilityWithIframe()
        result = await resource.doReadabilityFromUrl(url.url, signal)
        if (signal.aborted) {
          return
        }
        if (result.data.status === 'error') {
          const errMsg = result.data.message ?? 'Error importing content from the url!'
          if (errMsg === 'We expected to find HTML content but PDF detected') {
            url.type = ERemoteImportURLType.Pdf
            return await fetchFromUrl(url)
          }
          if (errMsg.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            url.type = ERemoteImportURLType.DocX
            return await fetchFromUrl(url)
          }
          track(trackErrorEventName, {
            url: url.url,
            info: errMsg,
          })
          url.status = ERemoteImportURLStatus.Error
          if (dndUrl.value) {
            track('project-creating-dnd-failed', {
              url: url.url,
              info: errMsg,
            })
          }
          snack.add(errMsg, { type: 'danger', permanent: true })
          return
        }
        url.content = prepareImportedHtml(result.data.content ?? '')
        url.title = result.data.title
      }

      url.status = ERemoteImportURLStatus.Fetched
      url.progress = 100

      if (url.content) {
        content.value = urls.value.map((u) => u.content ?? '').join('\n')
      }
      try {
        const title = result.data.title ? result.data.title : getTitleFromContent(result.data.content ?? '')
        draftTitle.value = title
        await queueDraftChange(async () => {
          if (signal.aborted) {
            return
          }
          const savedId = await drafts.save(
            draftId.value >= 0 ? draftId.value : null,
            title,
            content.value,
            result.data.url ?? '',
            resolveImportType(url),
            null,
            undefined,
            undefined,
            true,
          )
          if (!signal.aborted) {
            draftId.value = savedId
          }
        })
        if (signal.aborted) {
          return
        }
        track(trackEventName, {
          url: url.url,
        })
        if (dndUrl.value) {
          track('project-creating-dnd', {
            url: url.url,
          })
        }
        snack.add('Saved to doc')
      } catch {
        if (!signal.aborted) {
          snack.add('Error when saving to doc', { type: 'danger', permanent: true })
        }
      }
    } catch (e) {
      if (signal.aborted) {
        return
      }
      track(trackErrorEventName, {
        url: url.url,
        info: e instanceof Error ? e.message : 'Error reading from URL',
      })
      url.status = ERemoteImportURLStatus.Error
      if (dndUrl.value) {
        track('project-creating-dnd-failed', {
          url: url.url,
        })
      }
      snack.add('Error reading from URL', { type: 'danger', permanent: true })
    }
  }

  async function fetchFromUrl(url: TRemoteImportURL) {
    url.url = url.url.replace(/ /g, '%20')
    const dBox = new DropboxUrlUtil()
    if (dBox.isDropboxShareUrl(url.url)) {
      url.url = dBox.getDirectDownloadUrl(url.url)
    }
    const unsupportedMessage = getUnsupportedImportUrlMessage(url.url)
    if (unsupportedMessage) {
      const trackErrorMsg = ImportTypes.helpers[url.type]?.trackErrorMsg
      if (trackErrorMsg) {
        track(trackErrorMsg, { url: url.url, reason: unsupportedMessage })
      }
      snack.add(unsupportedMessage, { type: 'danger', permanent: true })
      url.status = ERemoteImportURLStatus.Error
      return false
    }
    url.url = getGoogleDriveDownloadUrl(url.url) ?? url.url
    if (!ImportTypes.urlValidate(url.url)) {
      snack.add('Wrong URL')
      return false
    }
    await importFromUrl(url)
  }

  function getProgressUrlForType(type: ERemoteImportURLType) {
    const tmp = new Date()
    const sesId = cookies.get('PHPSESSID')
    const urls = {
      [ERemoteImportURLType.Pdf]: '/assets/tmpPdfUpload/pdfProgress_' + sesId + '.json',
      [ERemoteImportURLType.DocX]: '/assets/tmpPdfUpload/docxProgress_' + sesId + '.json',
      [ERemoteImportURLType.Pptx]: '/assets/tmpPdfUpload/pptxProgress_' + sesId + '.json',
      [ERemoteImportURLType.GSlides]: '/assets/tmpPdfUpload/pptxProgress_' + sesId + '.json',
      [ERemoteImportURLType.Flipbook]: '/assets/tmpPdfUpload/pdfProgress_' + sesId + '.json',
      [ERemoteImportURLType.Audio]: '',
      [ERemoteImportURLType.Video]: '',
      [ERemoteImportURLType.YouTube]: '',
      [ERemoteImportURLType.Web]: '',
      [ERemoteImportURLType.GDoc]: '',
    }
    let url = urls[type]
    if (!url) {
      throw new Error('Error in getDownloadProgress. unknown type ' + type)
    }
    url = url + '?rand=' + tmp.getTime()
    return url
  }

  async function importFromUrl(url: TRemoteImportURL) {
    const filename = url.url.substring(url.url.lastIndexOf('/') + 1).split('.')
    filename.pop()
    const signal = importController.signal
    try {
      await importFile(false, filename.join('.'), url)
      if (signal.aborted) {
        return
      }
      if (dndUrl.value) {
        track('project-creating-dnd', {
          url: url.url,
        })
      }
    } catch (e) {
      if (dndUrl.value) {
        track('project-creating-dnd-failed', {
          url: url.url,
        })
      }
      const message = e instanceof HTTPError ? 'Error importing file' : (e instanceof Error ? e.message : 'Error importing file')
      importErrorId = snack.add(message, { type: 'danger', permanent: true })
    }
  }

  async function getDownloadProgress(url: TRemoteImportURL): Promise<boolean> {
    try {
      const res = await http.get<{
        total: number
        current: number
      }>(getProgressUrlForType(url.type))

      progressErrorCounts.set(url, 0)

      if (!res) {
        return true
      }

      if (res.total === res.current && res.total > 0) {
        url.progress = 100
        return true
      }

      if (res.total > 0) {
        url.progress = Math.round((100 / res.total) * res.current)
      }
      return false
    } catch (e) {
      const errorCount = (progressErrorCounts.get(url) ?? 0) + 1
      progressErrorCounts.set(url, errorCount)
      if (errorCount > MAX_PROGRESS_ERRORS) {
        console.log('Stopping progress polling after repeated read failures', e)
        return true
      }
      return false
    }
  }

  function resolveImportType(url: TRemoteImportURL): TDraftImportType | null {
    const creator = useCreatorStore()
    const bookType = creator.bookType?.name || 'ebook'

    if (url.type === ERemoteImportURLType.DocX) {
      return { type: 'from-docx', subType: 'docx', newDashboard: true, book_type: bookType }
    }
    if (url.type === ERemoteImportURLType.Pdf) {
      return { type: 'from-pdf', subType: 'pdf', newDashboard: true, book_type: bookType }
    }
    if (url.type === ERemoteImportURLType.Pptx) {
      return { type: 'from-pptx', subType: 'pptx', newDashboard: true, book_type: bookType }
    }
    if (url.type === ERemoteImportURLType.GDoc) {
      return { type: 'from-gdoc', subType: 'gdoc', newDashboard: true, book_type: bookType }
    }
    if (url.type === ERemoteImportURLType.GSlides) {
      return { type: 'from-gslides', subType: 'gslides', newDashboard: true, book_type: bookType }
    }
    if (url.type === ERemoteImportURLType.Web) {
      return { type: 'from-blog', book_type: bookType }
    }
    return null
  }

  async function handleAudioOrVideo(url: TRemoteImportURL) {
    if (isScheduledVideoUrl(url.url)) {
      url.status = ERemoteImportURLStatus.ReadyToTranscribe
      return
    }
    url.status = ERemoteImportURLStatus.Fetching
    const helper = ImportTypes.helpers[url.type]
    processingMessage.value = 'Fetching...'
    if (!isFileImportType(url.type) || !helper) return
    const signal = importController.signal
    const startedAt = new Date()
    try {
      const res = await http.post<{
        status: string
        uploadedFileInfo: TUploadedFileInfo
        content: string
        message?: string
      }>(
        helper.endPoint,
        {
          audioOrVideoUrl: url.url,
        },
        false,
        0,
        signal,
      )
      if (signal.aborted) {
        return
      }
      if (res?.status === 'error') {
        url.status = ERemoteImportURLStatus.Error
        throw new Error(res.message)
      }
      if (!res) {
        throw new Error('Failed to fetch file from URL')
      }
      url.info = res.uploadedFileInfo
      url.status = ERemoteImportURLStatus.ReadyToTranscribe
      track(
        helper.trackSuccessTag,
        audioVideoImportTrackingProperties({
          importType: url.type,
          url: url.url,
          info: url.info,
          startedAt,
          dragAndDrop: !!dndUrl.value,
        }),
      )
    } catch (e) {
      if (signal.aborted) {
        return
      }
      url.status = ERemoteImportURLStatus.Error
      const error = e instanceof Error ? e : new Error('Failed to fetch file from URL')
      track(
        helper.trackErrorMsg,
        audioVideoImportTrackingProperties({
          importType: url.type,
          url: url.url,
          startedAt,
          reason: error.message,
          dragAndDrop: !!dndUrl.value,
        }),
      )
      window?.Bugsnag?.notify(error, (event: { addMetadata: (key: string, data: object) => void }) => {
        event.addMetadata('handleAudioOrVideo', {
          urlType: url.type,
          urlUrl: url.url,
          endpoint: helper?.endPoint,
        })
      })
      throw error
    }
  }

  async function importFile(alreadyOnServer: boolean, realName: string, url: TRemoteImportURL) {
    const helper = ImportTypes.helpers[url.type]
    snack.remove(importErrorId)

    processingMessage.value = helper?.processingLoadingMessage ?? 'Converting ...'

    if (['audio', 'video', 'youtube'].includes(importType.value ?? '')) {
      await handleAudioOrVideo(url)
      return
    }

    if (!isFileImportType(url.type) || !helper) return
    const data = {
      fileUrl: url.url,
      fileLink: '',
      name: '',
      fileName: url.url ? url.url : '',
      description: '',
    }

    if (alreadyOnServer) {
      data.fileName = realName
      data.fileLink = url.fileLink || ''
    }

    if (url.type == ERemoteImportURLType.Flipbook) {
      const parts = realName.replace('.pdf', '').replace('.PDF', '').split('_')
      if (parts.length > 1) {
        parts.shift()
      }
      data.name = parts.join(' ')
    }

    url.status = ERemoteImportURLStatus.Processing
    processingMessage.value = 'Processing...'

    url.progress = 0
    if (!alreadyOnServer) {
      startProgressPolling(url)
    }
    const startAt = new Date()
    let failed = false
    let failureReason = ''
    let refused = false
    const signal = importController.signal
    try {
      let res: TImportResponse
      try {
        res = await http.post<TImportResponse>(helper.endPoint, data, false, 0, signal)
      } catch (e) {
        if (!(e instanceof HTTPError) || e.status !== 400) {
          throw e
        }

        refused = true
        res = { status: 'error', message: getHttpErrorMessage(e, 'Error importing file'), content: '' }
      }
      if (signal.aborted) {
        return
      }

      pdfImportIntoFlipbookOnly.value = false
      const messageStringPresent: boolean =
        typeof res.message === 'string' && typeof res.message.includes === 'function'

      if (
        res?.status === 'error' &&
        url?.type === 'flipbook' &&
        messageStringPresent &&
        res.message?.includes('Error importing pdf') &&
        res.message?.includes('too many pages')
      ) {
        pdfImportIntoFlipbookOnly.value = true
      } else if (res?.status === 'error') {
        url.status = ERemoteImportURLStatus.Error
        throw new Error(res.message)
      }
      if (!res) {
        throw new Error('Failed to fetch file from URL')
      }
      url.status = ERemoteImportURLStatus.Fetched
      if (helper.module !== 'flipbook') {
        url.content = [ERemoteImportURLType.Pptx, ERemoteImportURLType.GSlides].includes(url.type)
          ? res.content
          : prepareImportedHtml(res.content)
        if (url.content) {
          // Check if URL is in remoteImport.urls or if it's from localImport
          const isInRemoteUrls = urls.value.includes(url as any)
          if (isInRemoteUrls) {
            content.value = urls.value.map((u) => u.content ?? '').join('\n')
          } else {
            // URL is from localImport, set content directly
            content.value = url.content
          }
        }
      }

      uploadedFileName.value = data?.fileName
      uploadedFileLink.value = data?.fileLink

      if (helper.module === 'flipbook' && res?.id) {
        const flipbookUrl: string = await flipbooks.buildFlipbookUrl(res.id, res?.token ?? '', 'FP')

        await modal.open(() => import('@/components/modal/DesModalFlipbookCreated.vue'), {
          flipbookId: res.id,
          flipbookUrl,
        })
      } else if (helper.module === 'flipbook') {
        snack.add('Error creating flipbook')
      } else if (pdfImportIntoFlipbookOnly.value !== true) {
        // save to draft
        if (!(helper.module === 'flipbook')) {
          try {
            const name = res.title || (url.file?.name.split('.').slice(0, -1).join('.') ?? realName)
            if (!url.title) {
              url.title = name
            }
            const savedId = await drafts.save(null, name, url.content ?? '', url.url, resolveImportType(url))
            if (signal.aborted) {
              return
            }
            draftId.value = savedId
            draftTitle.value = name
            snack.add('Saved to doc')
          } catch {
            if (!signal.aborted) {
              snack.add('Error when saving to doc', { type: 'danger', permanent: true })
            }
          }
        }
      }
    } catch (e) {
      if (signal.aborted) {
        return
      }
      failed = true
      url.status = ERemoteImportURLStatus.Error
      if (dndUrl.value) {
        track('project-creating-dnd-failed', {
          url: url.url,
        })
      }
      const error = e instanceof Error ? e : new Error('Failed to fetch file from URL')
      failureReason = error.message
      if (refused) {
        throw error
      }
      window?.Bugsnag?.notify(error, (event: { addMetadata: (key: string, data: object) => void }) => {
        event.addMetadata('importFile', {
          urlType: url.type,
          urlUrl: url.url,
          fileLink: url.fileLink,
          alreadyOnServer,
          realName,
          endpoint: helper?.endPoint,
        })
      })
      throw error
    } finally {
      stopProgressTimer(url)
      if (!signal.aborted) {
        const intervalString = TimerUtils.computeAndFormatElapsedTime(startAt)
        const trackEventName = failed ? helper.trackErrorMsg : helper.trackSuccessTag
        track(trackEventName, {
          time: intervalString,
          url: url.url ?? '',
          ...(failed ? { reason: failureReason, refused } : {}),
        })
      }
      /*
            snack.add("todo - handle ui for fetched")
            */
    }
  }

  async function fetchUrl(url: TRemoteImportURL) {
    if (
      url.status === ERemoteImportURLStatus.Fetching ||
      url.status === ERemoteImportURLStatus.Fetched ||
      url.url.length === 0
    ) {
      return
    }
    if (url.type === ERemoteImportURLType.Web || url.type === ERemoteImportURLType.GDoc) {
      await fetchFromWeb(url)
    } else if (isFileImportType(url.type)) {
      await fetchFromUrl(url)
    } else {
      console.log('Unknown URL import type')
    }
  }

  async function importScheduledVideo(url: TRemoteImportURL, language: string, title?: string) {
    url.status = ERemoteImportURLStatus.Fetching
    if (url.url.includes('youtu.be')) {
      url.url = url.url.replace('https://youtu.be/', 'https://www.youtube.com/watch?v=')
    }
    loader.show('Checking video access, this can take a minute...')
    const trackingProperties = (reason?: string) => ({
      ...audioVideoImportTrackingProperties({
        importType: url.type,
        url: url.url,
        reason,
        dragAndDrop: !!dndUrl.value,
      }),
      language,
      ...(title ? { title } : {}),
    })
    try {
      const res = await http.post<{
        status: string
        message: string
      }>('/scheduleYoutubeImport', {
        url: url.url,
        ...(title ? { title } : {}),
        language,
      })
      if (res?.status === 'error') {
        track('error-scheduling-audio-video', trackingProperties(res.message))
        url.status = ERemoteImportURLStatus.Error
        throw new Error(res.message)
      }
      if (!res) {
        track('error-scheduling-audio-video', trackingProperties('Failed to queue video for transcription'))
        throw new Error('Failed to queue video for transcription')
      }
      track('scheduled-audio-video-transcription', trackingProperties())
      router.push('/docs/transcriptions')
      snack.add(
        "Your video has been queued for transcription.  Please check your email in a few minutes and we’ll notify you when it's ready.",
      )
    } catch (e) {
      url.status = ERemoteImportURLStatus.Error
      const friendly = !(e instanceof HTTPError) && e instanceof Error ? e.message : 'Failed to queue video for transcription'
      throw new Error(friendly, e instanceof Error ? { cause: e } : undefined)
    } finally {
      loader.hide()
    }
  }

  async function startTranscription(url: TRemoteImportURL, language: string = 'en') {
    if (url.url && isScheduledVideoUrl(url.url)) {
      await importScheduledVideo(url, url.language ?? language)
      return
    }
    try {
      const res = await http.post<{
        status: string
        message: string
        content: string
      }>('/recognize_speech_temi', {
        draftName: url.info?.title || url.info?.fileName || '',
        fileLink: url.info?.desTmpUrl ?? '',
        fileName: url.info?.fileName ?? '',
        importedFromUrl: url.info?.importedFromUrl ?? '',
        language: url.language ?? language,
        tmpFilePath: url.info?.desTmpFullPath ?? '',
        uniqueTimestamp: new Date().valueOf(),
      })
      if (res?.status === 'error') {
        url.status = ERemoteImportURLStatus.Error
        throw new Error(res.message)
      }
      if (!res) {
        throw new Error('Failed to schedule transcription')
      }
      snack.add('Transcription scheduled')
      router.push('/docs/transcriptions')
    } catch (e) {
      if (dndUrl.value) {
        track('project-creating-dnd-failed', {
          url: url.url,
        })
      }
      url.status = ERemoteImportURLStatus.Error
      const notifyError = e instanceof Error ? e : new Error('Failed to fetch file from URL', { cause: e })
      window?.Bugsnag?.notify(notifyError, (event: { addMetadata: (key: string, data: object) => void }) => {
        event.addMetadata('startTranscription', {
          urlType: url.type,
          urlUrl: url.url,
        })
      })
      if (e instanceof HTTPError) {
        throw new Error('Failed to start transcription. Please try again or contact support.', { cause: e })
      }
      throw notifyError
    }
  }

  async function setNewProjectData(
    draftIdParam: number | null,
    draftTitleParam: string,
    contentParam: string,
    tags: number[] | undefined,
    bookTypeParam?: string | null,
  ) {
    draftId.value = draftIdParam ?? -1
    draftTitle.value = draftTitleParam
    content.value = contentParam
    draftTags.value = tags ?? []
    bookType.value = bookTypeParam ?? null
    await http.post('/projects/import_proxy', {
      title: draftTitleParam,
      content: contentParam,
    })
  }

  return {
    content,
    urls,
    init,
    addUrl,
    uploadedFileName,
    uploadedFileLink,
    // addFiles,
    draftId,
    draftTitle,
    draftTags,
    bookType,
    importFile,
    dndUrl,
    dndUrlType,
    removeUrlOrFile,
    cancelImports,
    fetchUrl,
    isFileImportType,
    insertPageBreaksBeforeHeadings,
    startTranscription,
    setNewProjectData,
    pdfImportIntoFlipbookOnly,
    processingMessage,
  }
})
